import React, { useState } from "react";
import { Collapse, Card, CardHeader, CardBody } from "reactstrap";
import PropTypes from "prop-types";
import Markdown from "react-markdown";

/* =========================
   Accordion
========================= */

function Accordion({ children, components, darkMode, defaultOpened }) {
  const [openIndex, setOpenIndex] = useState(defaultOpened ?? null);
  const isDarkMode = !!darkMode;

  const toggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  // Normalize items (support both new JSX API and legacy 'components' array)
  const items = components
    ? components.map((c) => ({
      title: c.title,
      content: c.content ?? null,
      text: c.text ?? null,
    }))
    : React.Children.toArray(children)
      .filter((child) => {
        if (child.type !== AccordionItem) {
          if (process.env.NODE_ENV !== "production") {
            console.warn(
              "Accordion only accepts Accordion.Item as children."
            );
          }
          return false;
        }
        return true;
      })
      .map((child) => ({
        title: child.props.title,
        content: child.props.children,
      }));

  return (
    <div className="accordion">
      {items.map((item, index) => (
        <Card key={index} className="card-plain">
          <CardHeader
            role="tab"
            onClick={() => toggle(index)}
            className={`cursor-pointer ${isDarkMode ? "text-white" : ""}`}
            aria-expanded={openIndex === index}
          >
            <h4 className="mb-0">{item.title}</h4>
          </CardHeader>

          <Collapse isOpen={openIndex === index}>
            <CardBody>
              <div
                className={`line-styling-breaks ${isDarkMode ? "text-white" : ""
                  }`}
              >
                {item.content ? (
                  item.content
                ) : item.text ? (
                  <Markdown
                    components={{
                      a: ({ node, ...props }) => (
                        <a {...props} target="_blank" rel="noopener noreferrer" />
                      ),
                    }}
                  >
                    {item.text
                      .replace(/\r\n/g, "\n")
                      .replace(/\n{2,}/g, "\n\n")
                      .replace(/\n/g, "  \n")}
                  </Markdown>
                ) : null}
              </div>
            </CardBody>
          </Collapse>
        </Card>
      ))}
    </div>
  );
}

/* =========================
   Accordion.Item
========================= */

function AccordionItem({ children }) {
  return <>{children}</>; // Render children for JSX API
}

AccordionItem.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  children: PropTypes.node,
};

/* =========================
   PropTypes
========================= */

Accordion.propTypes = {
  darkMode: PropTypes.bool,
  defaultOpened: PropTypes.number,
  components: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      text: PropTypes.string,
      content: PropTypes.node,
    })
  ),
  children: PropTypes.node,
};

/* =========================
   Attach Item
========================= */

Accordion.Item = AccordionItem;

export default Accordion;